/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { VStack } from "@panda/jsx";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ResellersSearchSectionProps } from "./ResellersSearch.section";
import { ResellersSearchHeader } from "./ResellersSearchHeader.molecule";
import { ResellersSearchList } from "./ResellersSearchList.molecule";
import { getUniqueContinents } from "@components/utils/resellers";
import { useMessages } from "@epic-mw/i18n";

const pageSize = 18;
export type ResellersSearchMoleculeProps = Omit<
	ResellersSearchSectionProps,
	"config" | "cards2" | "cards3" | "cards4"
> & {
	triggerScroll: () => void;
};

export function ResellersSearchMolecule({
	cards,
	locationLinkTemplate,
	noResultsLabel,
	searchLabel,
	triggerScroll,
	viewLocationLabel,
	skipRouter = false,
}: ResellersSearchMoleculeProps): React.JSX.Element {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const messages = useMessages();

	const hasLoaded = useRef(false);
	const [query, setQuery] = useState("");

	const [currentPage, setCurrentPage] = useState<number>(parseInt(searchParams?.get("rsPage") || "1"));
	const [currentFilter, setFilter] = useState(searchParams?.get("rsFilter") || "");

	const [paramsPath, setParamsPath] = useState<string | undefined>();
	const [noFilterPath, setNoFilterPath] = useState("");

	const handleFilterChange = useCallback(
		(newFilter: string) => {
			setFilter((currentFilter) => {
				if (newFilter !== currentFilter) setCurrentPage(1);
				return newFilter;
			});
		},
		[setFilter, setCurrentPage],
	);

	useEffect(() => {
		if (hasLoaded.current) return;
		hasLoaded.current = true;

		const pageParam = parseInt(searchParams?.get("rsPage") || "1");
		const filterParam = searchParams?.get("rsFilter") || "";

		if (typeof filterParam === "string") handleFilterChange(filterParam);
		if (pageParam > 0) setCurrentPage(pageParam);
	}, [handleFilterChange, searchParams]);

	useEffect(() => {
		const params = new URLSearchParams(searchParams);

		if (currentPage > 1) {
			params.set("rsPage", `${currentPage}`);
		} else params.delete("rsPage");

		if (currentFilter?.length) {
			params.set("rsFilter", `${currentFilter}`);
		} else params.delete("rsFilter");

		const newParams = params?.toString()?.trim();
		setParamsPath(newParams);

		params.delete("rsFilter");
		const noFilterParams = params?.toString()?.trim();
		setNoFilterPath(`${pathname}${noFilterParams?.length ? `?${noFilterParams}` : ""}`);
	}, [searchParams, currentPage, currentFilter, query, pathname, setParamsPath, setNoFilterPath]);

	useEffect(() => {
		if (typeof paramsPath === "string") {
			const newPath = `${pathname}${paramsPath?.length ? `?${paramsPath}` : ""}`;
			if (!skipRouter) window.history.pushState({}, "", newPath);
			//slower alternative:
			//router.push(newPath, { scroll: false });
		}
	}, [pathname, paramsPath, skipRouter]);

	const handlePageChange = useCallback(
		(newPage: number) => {
			setCurrentPage(newPage);
			if (triggerScroll) triggerScroll();
		},
		[triggerScroll],
	);
	const searchFilters = useMemo(
		() =>
			getUniqueContinents(cards)?.map((id) => ({
				id,
				tabLabel: messages[`epic.cosmos.label.continent.${id}`] || "",
			})) || [],
		[cards, messages],
	);

	return (
		<VStack gap="eds.dimension.56">
			<ResellersSearchHeader
				onValueChange={setQuery}
				onFilterChange={setFilter}
				searchLabel={searchLabel}
				searchValue={query}
				currentFilter={currentFilter}
				noFilterPath={noFilterPath}
				searchFilters={searchFilters}
			/>
			<ResellersSearchList
				locationLinkTemplate={locationLinkTemplate}
				cards={cards}
				currentPage={currentPage}
				query={query}
				filter={currentFilter}
				pageSize={pageSize}
				handlePageChange={handlePageChange}
				viewLocationLabel={viewLocationLabel}
				noResultsLabel={noResultsLabel}
			/>
		</VStack>
	);
}
